import { FetchService, ServerService } from '@affine/core/modules/cloud';
import { UrlService } from '@affine/core/modules/url';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Shared Google-connection plumbing for every surface that can start the
 * OAuth popup. There are two such surfaces — Workspace Settings →
 * Integrations → Google, and the Share menu's "Send by email" compose dialog
 * — and they used to carry two near-identical copies of this listener that
 * had already drifted apart (differing staleness guards, differing teardown,
 * one re-checking status after a failure and one not). Anything genuinely
 * per-surface (notification copy, what a status error should mean) is a
 * callback, not a fork.
 */

// The backend can only postMessage back to the popup opener when it recognizes
// the OAuth state/nonce. If the nonce is expired or unrecognized it has no way
// to know a popup opened it at all, so it renders a plain error page and never
// posts anything. Without a bound, a user who leaves that popup open (or who
// closes it without noticing) would wait forever. We bound the wait instead of
// trying to detect the popup closing: `openPopupWindow` doesn't hand back a
// window reference, so `window.closed` polling isn't available uniformly. A
// flat timeout works in every environment.
export const GOOGLE_CONNECT_TIMEOUT_MS = 120_000;

/**
 * The popup's callback page has NO `window.opener` to postMessage back
 * through. The web app opens popups with
 * `window.open(url, '_blank', 'popup,noreferrer')`, and per the HTML spec's
 * window-open steps `noreferrer` implies `noopener` — verified in Chrome:
 * `popup` gives the popup an opener, `popup,noreferrer` gives it
 * `window.opener === null`, identically to `popup,noopener`. That is why the
 * Connect spinner used to run the full 120s even though the OAuth round trip
 * had plainly succeeded (YOD-431): the connection was made, the opener just
 * never heard about it.
 *
 * So the result arrives over channels that need no window reference:
 *
 * - `BroadcastChannel` (primary) — same-origin by construction, so it needs
 *   no origin check of its own, and delivery is queued on this window's event
 *   loop before the popup's `postMessage` returns, so the popup closing right
 *   after cannot drop it.
 * - a `localStorage` write (belt-and-braces) — synchronous and durable, and
 *   its `storage` event fires in every OTHER same-origin window. One extra
 *   listener buys cover for a BroadcastChannel message lost to renderer
 *   teardown, and the same trick is already in-repo in the GitHub integration
 *   panel ("fallback when window.opener is null").
 * - `postMessage` (kept) — costs nothing and still covers any future caller
 *   that opens the popup with a live opener.
 *
 * Whichever arrives first wins; the handler below is idempotent, so the
 * others are ignored.
 *
 * ⚠️ These two names are duplicated in
 * `packages/backend/server/src/plugins/google-workspace/constants.ts` — there
 * is no module both the backend and the web bundle can import. Change one,
 * change the other.
 */
const GOOGLE_CONNECT_CHANNEL = 'affine:google-connect';
const GOOGLE_CONNECT_STORAGE_KEY = 'affine:google-connect-result';

type GoogleConnectSignal = 'google-connected' | 'google-connect-failed';

function readSignal(data: unknown): GoogleConnectSignal | null {
  const type = (data as { type?: unknown } | null | undefined)?.type;
  return type === 'google-connected' || type === 'google-connect-failed'
    ? type
    : null;
}
// Only show the spinner once the user has been waiting a moment, so a fast
// popup round-trip doesn't produce a flash of loading UI.
const SPINNER_DELAY_MS = 1000;

export type GoogleConnectFailure =
  /** The callback page itself reported failure (`google-connect-failed`). */
  | { reason: 'rejected' }
  /** Nothing posted back within {@link GOOGLE_CONNECT_TIMEOUT_MS}. */
  | { reason: 'timeout' }
  /** `openPopupWindow` threw before the popup ever opened. */
  | { reason: 'popup-error'; error: unknown };

export interface UseGoogleConnectPopupOptions {
  onConnected?: () => void;
  onFailed?: (failure: GoogleConnectFailure) => void;
}

export interface UseGoogleConnectPopupResult {
  /** Must be called directly from a user click — popup blockers require it. */
  connect: () => void;
  /** True for the whole 0–120s window a popup flow is in flight. */
  connecting: boolean;
  /** True once `connecting` has held for ~1s. */
  showSpinner: boolean;
}

export function useGoogleConnectPopup({
  onConnected,
  onFailed,
}: UseGoogleConnectPopupOptions = {}): UseGoogleConnectPopupResult {
  const urlService = useService(UrlService);
  const serverService = useService(ServerService);

  const [connecting, setConnecting] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);

  // Holds the *state-free* teardown for the in-flight attempt, so unmount can
  // drop the listener and timers without calling setState on a gone
  // component. Doubles as the synchronous re-entrancy guard: a second
  // `connect()` while one is in flight can never open a second popup or stack
  // a second listener, independently of React state timing.
  const cleanupRef = useRef<(() => void) | null>(null);

  // Read through a ref so `connect` keeps a stable identity and callers don't
  // have to memoize their callbacks to avoid re-registering anything.
  const callbacksRef = useRef({ onConnected, onFailed });
  useEffect(() => {
    callbacksRef.current = { onConnected, onFailed };
  });

  useEffect(() => {
    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, []);

  const connect = useCallback(() => {
    if (cleanupRef.current) return;

    setConnecting(true);
    const spinnerTimer = setTimeout(
      () => setShowSpinner(true),
      SPINNER_DELAY_MS
    );

    // `BroadcastChannel` is missing in some non-browser test environments and
    // in older WebViews; the flow degrades to the other two channels there
    // rather than throwing at `connect()` time.
    let channel: BroadcastChannel | null = null;
    try {
      channel =
        typeof BroadcastChannel === 'function'
          ? new BroadcastChannel(GOOGLE_CONNECT_CHANNEL)
          : null;
    } catch {
      channel = null;
    }

    // Drops the listeners/channel/timers without touching state — used both
    // by `finish` below and directly on unmount, where setting state would be
    // pointless (the component is gone) and best avoided. Must stay
    // idempotent: `finish` calls it, and unmount may call it again.
    const teardown = () => {
      clearTimeout(spinnerTimer);
      clearTimeout(timeoutTimer);
      window.removeEventListener('message', handleMessage);
      window.removeEventListener('storage', handleStorage);
      if (channel) {
        // Closing on every exit path — success, failure, timeout, unmount —
        // is what keeps repeated connect attempts from stacking channels.
        try {
          channel.close();
        } catch {
          // A channel already closed by a previous teardown; nothing to do.
        }
        channel = null;
      }
    };

    // Three channels can deliver the same result, and the timeout can race
    // all of them. `settled` makes the completion happen exactly once:
    // whichever signal lands first wins, the rest are dropped. `teardown`
    // removes the listeners, but events already dispatched can still be
    // in flight, so the guard is explicit rather than implied by teardown.
    let settled = false;
    const finish = () => {
      if (settled) return false;
      settled = true;
      teardown();
      cleanupRef.current = null;
      setConnecting(false);
      setShowSpinner(false);
      return true;
    };

    const handleSignal = (signal: GoogleConnectSignal) => {
      if (!finish()) return;
      if (signal === 'google-connected') {
        callbacksRef.current.onConnected?.();
      } else {
        callbacksRef.current.onFailed?.({ reason: 'rejected' });
      }
    };

    const handleMessage = (event: MessageEvent) => {
      // Load-bearing: the payload is only ever read after the origin matches
      // ours. A page on another origin can postMessage into this window
      // freely, and `{ type: 'google-connected' }` is trivially forgeable.
      // (BroadcastChannel and `storage` below need no equivalent check —
      // both are same-origin by construction, and a MessageEvent arriving on
      // a BroadcastChannel carries an empty `origin`, so applying this check
      // to them would reject every real signal.)
      if (event.origin !== window.location.origin) return;
      const signal = readSignal(event.data);
      if (signal) handleSignal(signal);
    };

    const handleChannelMessage = (event: MessageEvent) => {
      const signal = readSignal(event.data);
      if (signal) handleSignal(signal);
    };

    const handleStorage = (event: StorageEvent) => {
      if (event.key !== GOOGLE_CONNECT_STORAGE_KEY || !event.newValue) return;
      let signal: GoogleConnectSignal | null = null;
      try {
        signal = readSignal(JSON.parse(event.newValue));
      } catch {
        return;
      }
      if (!signal) return;
      // Consume it, so a stale entry cannot be re-read by a later attempt and
      // so we don't leave the result of an OAuth flow sitting in storage.
      try {
        localStorage.removeItem(GOOGLE_CONNECT_STORAGE_KEY);
      } catch {
        // Storage disabled/full — the signal itself already arrived.
      }
      handleSignal(signal);
    };

    const timeoutTimer = setTimeout(() => {
      if (!finish()) return;
      callbacksRef.current.onFailed?.({ reason: 'timeout' });
    }, GOOGLE_CONNECT_TIMEOUT_MS);

    window.addEventListener('message', handleMessage);
    window.addEventListener('storage', handleStorage);
    if (channel) {
      // `onmessage` rather than `addEventListener`: the reference is dropped
      // with the channel in `teardown`, so there is nothing left to unbind.
      channel.onmessage = handleChannelMessage;
    }
    cleanupRef.current = teardown;

    try {
      // `openPopupWindow` requires a full http(s) URL (it throws otherwise),
      // so resolve against the server's base URL rather than passing the
      // route path directly.
      const connectUrl = new URL(
        '/api/google/connect?mode=popup',
        serverService.server.serverMetadata.baseUrl
      ).toString();
      // NOTE: `openPopupWindow` is optional-chained on the UrlService's popup
      // provider — with no provider registered this is a silent no-op, and
      // the flow simply times out after 120s. In Electron the provider shells
      // out to the *system browser* — a different browser process with its
      // own storage partition — so none of the three channels above can reach
      // this window and that flow also ends in the timeout. Web (the shipped
      // target for YOD-426) is unaffected. Deliberately not given an Electron fallback
      // here — that needs a real IPC round-trip, tracked separately.
      urlService.openPopupWindow(connectUrl);
    } catch (error) {
      finish();
      callbacksRef.current.onFailed?.({ reason: 'popup-error', error });
    }
  }, [serverService, urlService]);

  return { connect, connecting, showSpinner };
}

export type GoogleConnectionStatus =
  | 'loading'
  | 'connected'
  | 'not-connected'
  | 'reconnect-required'
  /** The status call itself failed; callers decide what that should mean. */
  | 'error';

interface GoogleStatusResponse {
  connected: boolean;
  reason?: 'GOOGLE_NOT_CONNECTED' | 'GOOGLE_RECONNECT_REQUIRED';
}

export interface UseGoogleConnectionStatusOptions {
  /**
   * When false, no status call is made and the status stays `'loading'` —
   * used by the compose dialog, which should only ask while it is open.
   */
  enabled?: boolean;
  onError?: (error: unknown) => void;
}

export interface UseGoogleConnectionStatusResult {
  status: GoogleConnectionStatus;
  /** Re-reads `GET /api/google/status`. Never rejects. */
  refresh: () => Promise<void>;
}

export function useGoogleConnectionStatus({
  enabled = true,
  onError,
}: UseGoogleConnectionStatusOptions = {}): UseGoogleConnectionStatusResult {
  const fetchService = useService(FetchService);
  const [status, setStatus] = useState<GoogleConnectionStatus>('loading');

  const onErrorRef = useRef(onError);
  useEffect(() => {
    onErrorRef.current = onError;
  });

  // Guards against a stale response (e.g. a refresh triggered right before
  // unmount, or one connect attempt's timeout firing after a later attempt
  // already resolved) from clobbering more recent state.
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    try {
      const res = await fetchService.fetch('/api/google/status');
      const data: GoogleStatusResponse = await res.json();
      if (requestIdRef.current !== requestId) return;
      if (data.connected) {
        setStatus('connected');
      } else if (data.reason === 'GOOGLE_RECONNECT_REQUIRED') {
        setStatus('reconnect-required');
      } else {
        setStatus('not-connected');
      }
    } catch (error) {
      if (requestIdRef.current !== requestId) return;
      setStatus('error');
      onErrorRef.current?.(error);
    }
  }, [fetchService]);

  useEffect(() => {
    if (!enabled) {
      // Invalidate any in-flight response so it can't land after the caller
      // has stopped caring (the dialog closing, say).
      requestIdRef.current++;
      setStatus('loading');
      return;
    }
    refresh().catch(console.error);
  }, [enabled, refresh]);

  return { status, refresh };
}
